<script>
import SectionCard from '../SectionCard.vue';
import { money } from '../helpers';

export default {
  name: 'OverviewSection',
  components: { SectionCard },
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  computed: {
    rollup() {
      return this.project.rollup;
    },
    priceRangeLabel() {
      if (this.rollup.mixed_currency)
        return this.$t('REAL_ESTATE.OVERVIEW.MIXED_CURRENCY_NOTE');
      if (this.rollup.price_min == null) return '—';
      if (this.rollup.price_min === this.rollup.price_max) {
        return money(this.rollup.price_min, this.rollup.currency);
      }
      return `${money(this.rollup.price_min, this.rollup.currency)} – ${money(
        this.rollup.price_max,
        this.rollup.currency
      )}`;
    },
    startingPriceLabel() {
      if (this.rollup.mixed_currency) return '—';
      if (this.rollup.price_min == null) return '—';
      return money(this.rollup.price_min, this.rollup.currency);
    },
  },
};
</script>

<template>
  <SectionCard section-id="overview" :title="$t('REAL_ESTATE.OVERVIEW.TITLE')">
    <template #action>
      <woot-button
        variant="clear"
        size="small"
        icon="edit"
        @click="$emit('edit')"
      >
        {{ $t('REAL_ESTATE.OVERVIEW.EDIT_BUTTON') }}
      </woot-button>
    </template>

    <div class="flex flex-wrap gap-2 mb-3">
      <span
        v-if="project.project_code"
        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-mono font-medium bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400"
      >
        {{ project.project_code }}
      </span>
      <span
        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-woot-50 dark:bg-woot-900/40 text-woot-600 dark:text-woot-300"
      >
        {{ project.property_type }}
      </span>
      <span
        v-if="project.property_subtype"
        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
      >
        {{ project.property_subtype }}
      </span>
      <span
        v-if="project.marketing_badge"
        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300"
      >
        {{ project.marketing_badge }}
      </span>
    </div>

    <p
      v-if="project.short_description"
      class="text-sm text-slate-600 dark:text-slate-300 mb-4"
    >
      {{ project.short_description }}
    </p>

    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
      <div class="bg-slate-50 dark:bg-slate-900/40 rounded-lg p-3">
        <p class="text-xs text-slate-400 mb-1">
          {{ $t('REAL_ESTATE.OVERVIEW.STARTING_PRICE') }}
        </p>
        <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {{ startingPriceLabel }}
        </p>
      </div>
      <div class="bg-slate-50 dark:bg-slate-900/40 rounded-lg p-3">
        <p class="text-xs text-slate-400 mb-1">
          {{ $t('REAL_ESTATE.OVERVIEW.PRICE_RANGE') }}
        </p>
        <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {{ priceRangeLabel }}
        </p>
      </div>
      <div class="bg-slate-50 dark:bg-slate-900/40 rounded-lg p-3">
        <p class="text-xs text-slate-400 mb-1">
          {{ $t('REAL_ESTATE.OVERVIEW.FLOOR_PLANS_COUNT') }}
        </p>
        <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {{ rollup.floor_plan_count }}
        </p>
      </div>
    </div>

    <p
      v-if="project.long_description"
      class="text-sm text-slate-600 dark:text-slate-300 mb-4 whitespace-pre-line"
    >
      {{ project.long_description }}
    </p>

    <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
      <div v-if="project.developer">
        <dt class="text-slate-400">
          {{ $t('REAL_ESTATE.OVERVIEW.DEVELOPER') }}
        </dt>
        <dd class="text-slate-900 dark:text-slate-100">
          {{ project.developer }}
        </dd>
      </div>
      <div>
        <dt class="text-slate-400">
          {{ $t('REAL_ESTATE.OVERVIEW.DEFAULT_CURRENCY') }}
        </dt>
        <dd class="text-slate-900 dark:text-slate-100">
          {{ project.currency || 'RM' }}
        </dd>
      </div>
      <div v-if="project.cover_image_url">
        <dt class="text-slate-400">
          {{ $t('REAL_ESTATE.OVERVIEW.COVER_IMAGE') }}
        </dt>
        <dd>
          <a
            :href="project.cover_image_url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-woot-500 hover:underline"
          >
            {{ $t('REAL_ESTATE.OVERVIEW.VIEW_IMAGE') }}
          </a>
        </dd>
      </div>
      <div v-if="project.website_url">
        <dt class="text-slate-400">{{ $t('REAL_ESTATE.OVERVIEW.WEBSITE') }}</dt>
        <dd>
          <a
            :href="project.website_url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-woot-500 hover:underline"
          >
            {{ project.website_url }}
          </a>
        </dd>
      </div>
    </dl>
  </SectionCard>
</template>
